import React from 'react'
import { graphql } from 'gatsby'
import { mergePrismicPreviewData } from 'gatsby-source-prismic'
import { Image, BlogPostHeader, BlogNav, BlogPostCta, PostGrid, Section, SEO, Share, Link } from 'newComponents'
import BlogUtm from '../utils/BlogUtm'
import Layout from '../components/layout.js'
import truncateString from '../utils/truncateString'
import stars from '../images/static-stars.jpg'
import * as Slices from '../slices'
import './blog_post.scss'

const IS_BROWSER = typeof window !== 'undefined'

export const Slice = ({ data, location }) => {
  const { slice_type: sliceType } = data

  const allSlices = { ...Slices }
  // Convert slice type from snake_case to TitleCase
  if (!sliceType) return null
  const sliceName = sliceType
    .split('_')
    .map((item) => item.charAt(0).toUpperCase() + item.substring(1))
    .join('')

  const CustomSlice = allSlices[sliceName]
  return <CustomSlice data={data} location={location} isBlog />
}

const BlogPost = ({ data: staticData, location }) => {
  const previewData = IS_BROWSER && window.__PRISMIC_PREVIEW_DATA__
  const data = mergePrismicPreviewData({ staticData, previewData })

  const { blogPost, suggestedPosts = {}, excludedCategories = {}, blogCtas = {} } = data
  const { edges: excludedArray = [] } = excludedCategories
  const { data: pageData, dataString, uid } = blogPost
  const { nodes: suggested = [] } = suggestedPosts
  const { data: ctaData = {} } = blogCtas

  const {
    ctas_active: ctasActive,
    side_bar_cta_image: sideCtaImage,
    side_bar_cta_link: sideCtaLink,
    top_cta_image: topCtaImage,
    top_cta_link: topCtaLink,
    bottom_cta_image: bottomCtaImage,
    bottom_cta_link: bottomCtaLink
  } = ctaData

  const excludedCategoriesArray = []

  excludedArray.forEach((category) => {
    const { node } = category
    const { uid: slug } = node
    excludedCategoriesArray.push(slug)
  })

  const {
    body: sliceData,
    metaTitle,
    metaDesc,
    ogImage,
    title,
    excerpt,
    featured_image: heroImage,
    categories,
    timestamp,
    hide_date: hideDate,
    author,
    body
  } = pageData

  const filteredCategories = []
  categories.forEach((category) => {
    const { category: cat } = category
    const { uid: slug } = cat
    if (!excludedCategoriesArray.includes(slug)) {
      filteredCategories.push(category)
    }
  })

  // back up meta desc
  const { primary } = body[0]
  const descBackupText = primary?.wysiwyg?.text

  const descBackup = truncateString(descBackupText, 85)

  const seo = {
    title: metaTitle?.text || title?.text,
    description: metaDesc?.text || excerpt?.text || descBackup,
    ogImage: ogImage?.url
  }

  const authorObject = author?.document?.data || {}
  const { author_name: authorName, author_image: authorImage } = authorObject
  const authorText = authorName?.text
  const hasAuthorImage = authorImage?.url

  const suggestedPostsData = suggested.slice(0, 6)

  return (
    <Layout staticNavbar>
      <SEO {...seo} />
      <div className="blog-post">
        <BlogNav />
        <section className="blog-post-body">
          {/* TOP CTA */}
          <BlogPostCta active={ctasActive} image={topCtaImage} link={topCtaLink?.url} />
          <BlogPostHeader
            title={title}
            categories={filteredCategories}
            timestamp={timestamp}
            hideDate={hideDate}
            author={author}
            dataString={dataString}
            location={location}
          />
          <Section className="blog-post-image">
            <Image image={heroImage?.localFile || heroImage?.url || stars} className="blog-post-hero-image" />
          </Section>
          <div className="blog-post-content-container">
            {/* Sidebar CTA */}
            <BlogPostCta isSidebar active={ctasActive} image={sideCtaImage} link={sideCtaLink?.url} />
            <div
              className={`blog-post-slice-content ${
                ctasActive && bottomCtaImage?.url && bottomCtaLink.url ? 'has-cta' : ''
              }`}
            >
              {sliceData.map((slice) => (
                <Slice key={slice.id} data={{ ...slice, uid }} location={location} />
              ))}
            </div>
            <BlogPostCta isBottom active={ctasActive} image={bottomCtaImage} link={bottomCtaLink?.url} />
          </div>
          <Section className="blog-post-author-container">
            <div className="blog-post-author-details">
              {authorText && (
                <Link to={`/blog/author/${author?.uid}`} className="blog-post-author-details-link">
                  {hasAuthorImage && <Image className="blog-post-author-image" image={authorImage} />}
                  {authorText && <span className="blog-post-author-name">{`By ${authorText}`}</span>}
                </Link>
              )}
            </div>
            <Share location={location} title={title.text} id="blog-share-footer" />
          </Section>
        </section>
        {/* BOTTOM CTA */}
        {suggestedPostsData.length === 6 && (
          <PostGrid posts={suggestedPostsData} title="You May Like" className="suggested-variant" />
        )}
      </div>
      <BlogUtm />
    </Layout>
  )
}

export default BlogPost

export const blogPostQuery = graphql`
  query BlogPostBySlug($uid: String!) {
    blogPost: prismicBlogPost(uid: { eq: $uid }) {
      id
      uid
      prismicId
      dataString
      data {
        metaTitle: meta_title {
          text
        }
        metaDesc: meta_description {
          text
        }
        ogImage: open_graph_image {
          url
        }
        title {
          text
          html
        }
        featured_image {
          url
        }
        categories {
          category {
            uid
            id
          }
        }
        author {
          uid
          id
          document {
            ... on PrismicAuthor {
              id
              data {
                author_name {
                  text
                  html
                }
                author_image {
                  url
                  localFile {
                    childImageSharp {
                      fluid(maxWidth: 200, quality: 90) {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                  }
                }
              }
              uid
            }
          }
        }
        excerpt {
          text
          html
        }
        timestamp(formatString: "MMMM D, YYYY")
        hide_date
        body {
          ...BlogPostWysiwyg
          ...BlogPostBreakoutQuote
          ...BlogPostTable
          ...BlogPostCodeGist
          ...BlogPostImage
          ...BlogPostSimpleQuote
          ...BlogPostYouTubeVideo
          ...BlogPostSoundcloud
          ...BlogPostHtmlTable
          ...BlogPostBlogCta
        }
      }
    }
    suggestedPosts: allPrismicBlogPost(filter: { uid: { ne: $uid } }, sort: { order: DESC, fields: data___timestamp }) {
      nodes {
        uid
        id
        data {
          categories {
            category {
              uid
              id
            }
          }
          title {
            html
            text
          }
          featured_image {
            url
          }
          excerpt {
            html
            text
          }
          timestamp(formatString: "MMMM D, YYYY")
        }
      }
    }
    excludedCategories: allPrismicCategory(filter: { data: { nav_exclude: { eq: true } } }) {
      edges {
        node {
          id
          uid
        }
      }
    }
    blogCtas: prismicSettings {
      data {
        ctas_active
        side_bar_cta_image {
          url
          localFile {
            childImageSharp {
              fluid(maxWidth: 2000, quality: 90) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
        side_bar_cta_link {
          url
        }
        top_cta_image {
          url
          localFile {
            childImageSharp {
              fluid(maxWidth: 2000, quality: 90) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
        top_cta_link {
          url
        }
        bottom_cta_image {
          url
          localFile {
            childImageSharp {
              fluid(maxWidth: 2000, quality: 90) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
        bottom_cta_link {
          url
        }
      }
    }
  }
`
